// components/SearchFilters.jsx
import React, { useState, useEffect } from 'react';
import './SearchFilters.css';

const SearchFilters = ({ category, onFilterChange, currentFilters }) => {
  const [localFilters, setLocalFilters] = useState(currentFilters);
  const [priceRange, setPriceRange] = useState({
    min: currentFilters.minPrice || '',
    max: currentFilters.maxPrice || ''
  });

  useEffect(() => {
    setLocalFilters(currentFilters);
    setPriceRange({
      min: currentFilters.minPrice || '',
      max: currentFilters.maxPrice || ''
    });
  }, [currentFilters]);

  const handleFilterUpdate = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handlePriceChange = (type, value) => {
    const newPriceRange = { ...priceRange, [type]: value };
    setPriceRange(newPriceRange);
    
    const newFilters = { 
      ...localFilters, 
      minPrice: newPriceRange.min,
      maxPrice: newPriceRange.max
    };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const resetFilters = {};
    setLocalFilters(resetFilters);
    setPriceRange({ min: '', max: '' });
    onFilterChange(resetFilters);
  };

  const getPriceRangeForCategory = () => {
    switch(category) {
      case 'property': return { min: 0, max: 10000000 };
      case 'hotel': return { min: 0, max: 1000000 };
      case 'service': return { min: 0, max: 1000000 };
      default: return { min: 0, max: 1000000 };
    }
  };

  const { min: categoryMin, max: categoryMax } = getPriceRangeForCategory();

  return (
    <div className="search-filters">
      <div className="filters-header">
        <h3>Filters</h3>
        <button className="clear-filters" onClick={clearFilters}>
          Clear All
        </button>
      </div>

      {/* Price Range Filter */}
      <div className="filter-group">
        <h4>Price Range</h4>
        <div className="price-inputs">
          <input
            type="number"
            placeholder="Min"
            value={priceRange.min}
            onChange={(e) => handlePriceChange('min', e.target.value)}
            min="0"
            max={categoryMax}
          />
          <span>-</span>
          <input
            type="number"
            placeholder="Max"
            value={priceRange.max}
            onChange={(e) => handlePriceChange('max', e.target.value)}
            min="0"
            max={categoryMax}
          />
        </div>
        <div className="price-slider">
          <input
            type="range"
            min="0"
            max={categoryMax}
            value={priceRange.max || categoryMax}
            onChange={(e) => handlePriceChange('max', e.target.value)}
          />
        </div>
      </div>

      {/* Location Filter — works across all categories (property, hotel, service) */}
      <div className="filter-group">
        <h4>Location</h4>
        <input
          type="text"
          placeholder="Enter city or area"
          value={localFilters.location || ''}
          onChange={(e) => handleFilterUpdate('location', e.target.value)}
        />
      </div>

      {/* Category-specific filters */}
      {category === 'property' && (
        <>
          <div className="filter-group">
            <h4>Property Type</h4>
            <select
              value={localFilters.categoryType || ''}
              onChange={(e) => handleFilterUpdate('categoryType', e.target.value)}
            >
              <option value="">Any Type</option>
              <option value="house">House</option>
              <option value="apartment">Apartment</option>
              <option value="villa">Villa</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>

          <div className="filter-group">
            <h4>Bedrooms</h4>
            <select
              value={localFilters.bedrooms || ''}
              onChange={(e) => handleFilterUpdate('bedrooms', e.target.value)}
            >
              <option value="">Any</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
              <option value="5">5+</option>
            </select>
          </div>
        </>
      )}

      {category === 'hotel' && (
        <div className="filter-group">
          <h4>Star Rating</h4>
          {[5, 4, 3, 2, 1].map((rating) => (
            <label key={rating} className="rating-filter">
              <input
                type="checkbox"
                checked={localFilters.rating === rating.toString()}
                onChange={() => handleFilterUpdate('rating', 
                  localFilters.rating === rating.toString() ? '' : rating.toString()
                )}
              />
              <span className="rating-stars">
                {Array(rating).fill().map((_, i) => (
                  <i key={i} className="fa fa-star"></i>
                ))}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Amenities Filter — for hotels and service offers */}
      {(category === 'hotel' || category === 'service' || category === 'all') && (
        <div className="filter-group">
          <h4>Amenities</h4>
          {['WiFi', 'Pool', 'Gym', 'Spa', 'Restaurant', 'Parking'].map((amenity) => (
            <label key={amenity} className="amenity-filter">
              <input
                type="checkbox"
                checked={localFilters.amenities?.includes(amenity) || false}
                onChange={(e) => {
                  const currentAmenities = localFilters.amenities || [];
                  const newAmenities = e.target.checked
                    ? [...currentAmenities, amenity]
                    : currentAmenities.filter(a => a !== amenity);
                  handleFilterUpdate('amenities', newAmenities);
                }}
              />
              <span>{amenity}</span>
            </label>
          ))}
        </div>
      )}

      {/* Date Filters for Hotels */}
      {category === 'hotel' && (
        <div className="filter-group">
          <h4>Date Range</h4>
          <input
            type="date"
            placeholder="Check-in"
            value={localFilters.checkIn || ''}
            onChange={(e) => handleFilterUpdate('checkIn', e.target.value)}
          />
          <input
            type="date"
            placeholder="Check-out"
            value={localFilters.checkOut || ''}
            onChange={(e) => handleFilterUpdate('checkOut', e.target.value)}
          />
        </div>
      )}
    </div>
  );
};

export default SearchFilters;